// Campana de notificaciones del header: dropdown con la lista + un toast
// (justArrived) cuando llega una notificación nueva mientras la app está abierta.
import { useState } from "react";
import { Bell, AlertTriangle, X, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNotifications } from "../hooks/useNotifications";

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.floor(h / 24)} d`;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead, justArrived, dismissJustArrived, deleteNotification } = useNotifications();
  const [open, setOpen] = useState(false);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 640;

  return (
    <>
      <div className="notification-shell">
        <button className="btn-icon notification-trigger" onClick={() => setOpen(o => !o)} title="Notificaciones">
          <Bell size={18} />
          {unreadCount > 0 && (
            <span className="notification-badge">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>

        <AnimatePresence>
          {open && (
            <>
              <div style={{ position: "fixed", inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.97 }}
                transition={{ duration: 0.15 }}
                className="notification-panel"
                style={isMobile ? {
                  position: "fixed",
                  top: 76,
                  left: 12,
                  right: 12,
                  width: "auto",
                  maxHeight: "min(70vh, 520px)",
                } : undefined}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
                  <strong style={{ fontSize: "0.85rem" }}>Notificaciones</strong>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} style={{ background: "none", border: "none", color: "var(--primary)", fontSize: "0.72rem", cursor: "pointer", fontWeight: 600 }}>
                      Marcar todas leídas
                    </button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <p style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: "0.8rem" }}>Sin notificaciones</p>
                ) : (
                  notifications.map(n => (
                    <div key={n.id}
                      onClick={() => !n.read && markRead(n.id)}
                      style={{
                        display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px", cursor: n.read ? "default" : "pointer",
                        background: n.read ? "transparent" : "#FEF2F2", borderBottom: "1px solid var(--border)",
                      }}>
                      <AlertTriangle size={16} style={{ color: "var(--danger)", flexShrink: 0, marginTop: 2 }} />
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: "0.78rem", fontWeight: n.read ? 500 : 700, color: "var(--text)" }}>{n.message}</p>
                        <p style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginTop: 2 }}>{timeAgo(n.created_at)}</p>
                      </div>
                      {n.read && (
                        <button
                          onClick={e => { e.stopPropagation(); deleteNotification(n.id); }}
                          title="Eliminar notificación"
                          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4, flexShrink: 0 }}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {justArrived && (
          <motion.div
            initial={{ opacity: 0, y: -16, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -16, x: "-50%" }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            style={{
              position: "fixed", top: 20, left: "50%", zIndex: 100,
              display: "flex", alignItems: "center", gap: 10,
              background: "var(--danger)", color: "#fff", padding: "12px 18px", borderRadius: "var(--radius)",
              boxShadow: "var(--shadow)", maxWidth: isMobile ? "calc(100vw - 24px)" : 420,
            }}
          >
            <AlertTriangle size={20} style={{ flexShrink: 0 }} />
            <p style={{ fontSize: "0.85rem", fontWeight: 700 }}>{justArrived.message}</p>
            <button onClick={dismissJustArrived} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", marginLeft: 4 }}>
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
